import { Button, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataModelConfiguration } from "../../../../api/types/dataModelConfigurationTypes";
import { useConfigurationsGridActionsContext } from "./ConfigurationsGridActionsContext";
import { getInitialEditConfigurationState, isFormDirty, updateNameAction } from "./editConfigurationState";

interface Props {
  editedConfiguration: DataModelConfiguration;
  onClose: () => void;
  onUpdated: (updatedConfiguration: DataModelConfiguration) => void;
}

const updateConfiguration = withErrorHandling(adminApi.updateDataModelConfiguration);

const EditConfigurationDialog = ({ editedConfiguration, onClose, onUpdated }: Props) => {
  const { configurations } = useConfigurationsGridActionsContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(() =>
    getInitialEditConfigurationState(configurations, editedConfiguration)
  );

  const handleSaveClick = async () => {
    setLoading(true);
    const [updatedConfiguration, error] = await updateConfiguration(editedConfiguration.id, {
      name: formState.name.trim(),
    });
    setLoading(false);

    if (error) {
      logError(error, "[EditConfigurationDialog] handleSaveClick");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Configuration saved successfully");
    onUpdated(updatedConfiguration);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Rename Configuration</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <TextField
          label="Name"
          value={formState.name}
          onChange={(e) => setFormState(updateNameAction(e.target.value))}
          error={!formState.validationResult.isValid}
          helperText={formState.validationResult.error}
          fullWidth
          autoFocus
        />
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          loading={loading}
          onClick={handleSaveClick}
          disabled={!formState.validationResult.isValid || !isFormDirty(formState)}
        >
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditConfigurationDialog;
